import React, { useState, useEffect } from 'react';
import axios, { AxiosError } from 'axios';
import { useOrganization } from '../../contexts/OrganizationContext';
import { Department } from '../../types';
import Select from 'react-select';

interface InviteUserProps {
  onUserInvited: (userData?: any, shouldClose?: boolean) => void;
  organizationId: string;
}

export default function InviteUser({ onUserInvited, organizationId }: InviteUserProps) {
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [departments, setDepartments] = useState<Department[]>([]);
  const [role, setRole] = useState<'admin' | 'manager' | 'employee'>('employee');
  const [message, setMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [messageType, setMessageType] = useState<'success' | 'info'>('info');

  const [isLoading, setIsLoading] = useState(false);
  const [keepModalOpen, setKeepModalOpen] = useState(false);

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await axios.get(`/api/organizations/${organizationId}/departments`);
        setDepartments(response.data);
      } catch (err) {
        console.error('Error fetching departments:', err);
      }
    };

    fetchDepartments();
  }, [organizationId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage('');
    setError(null);
    setMessageType('info');
    setIsLoading(true);

    try {
      const response = await axios.post(`/api/organizations/${organizationId}/invite`, {
        email,
        username,
        role,
        department_id: selectedDepartment || null
      });
      
      // Check if this is a true success (new user invitation) or just an informational message
      const responseMessage = response.data.message;
      
      if (responseMessage.includes('Invitation sent successfully') || 
          responseMessage.includes('User added to organization')) {
        // This is a true success - clear the form
        setEmail('');
        setUsername('');
        setMessage(responseMessage);
        setMessageType('success');
        
        // Prepare user data for optimistic update
        const userData = {
          email,
          name: username || 'Pending Registration',
          role,
          department_id: selectedDepartment || null,
          department_name: departments.find(d => d.id === selectedDepartment)?.name || null
        };
        
        if (!keepModalOpen) {
          onUserInvited(userData, true); // Close modal
        } else {
          // If keeping modal open, still call the callback to update the list
          onUserInvited(userData, false); // Keep modal open
        }
      } else {
        // This is an informational message (user already exists, etc.) - don't clear form
        setMessage(responseMessage);
        setMessageType('info');
        // Still update the user list to reflect any changes, but don't close modal
        onUserInvited(undefined, false);
      }
    } catch (err) {
      const error = err as AxiosError<{ error: string }>;
      const errorMessage = error.response?.data?.error || 'Failed to invite user.';
      setError(errorMessage);
      
      // Don't clear form on error - let user fix the issue
      // Don't call onUserInvited on error
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="p-4 bg-white rounded-lg shadow">
      <h2 className="text-xl font-semibold mb-4">Invite User</h2>
      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label htmlFor="email" className="block text-sm font-medium text-gray-700">
            Email Address
          </label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (error) setError(null);
              if (message) {
                setMessage('');
                setMessageType('info');
              }
            }}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            required
          />
        </div>

        <div className="mb-4">
          <label htmlFor="username" className="block text-sm font-medium text-gray-700">
            Name (Optional)
          </label>
          <input
            type="text"
            id="username"
            value={username}
            onChange={(e) => {
              setUsername(e.target.value);
              if (error) setError(null);
              if (message) {
                setMessage('');
                setMessageType('info');
              }
            }}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            placeholder="Will be overridden during registration"
          />
        </div>

        <div className="mb-4">
          <label htmlFor="department" className="block text-sm font-medium text-gray-700">
            Department
          </label>
          <Select
            id="department"
            value={selectedDepartment ? { value: selectedDepartment, label: departments.find(d => d.id === selectedDepartment)?.name || '' } : null}
            onChange={(option) => {
              setSelectedDepartment(option?.value || '');
              if (error) setError(null);
              if (message) {
                setMessage('');
                setMessageType('info');
              }
            }}
            options={[
              { value: '', label: 'No Department' },
              ...departments.map((dept) => ({
                value: dept.id,
                label: dept.name
              }))
            ]}
            isSearchable
            className="mt-1 block w-full"
          />
        </div>

        <div className="mb-4">
          <label htmlFor="role" className="block text-sm font-medium text-gray-700">
            Role
          </label>
          <select
            id="role"
            value={role}
            onChange={(e) => {
              setRole(e.target.value as 'admin' | 'manager' | 'employee');
              if (error) setError(null);
              if (message) {
                setMessage('');
                setMessageType('info');
              }
            }}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          >
            <option value="employee">Employee</option>
            <option value="manager">Manager</option>
            <option value="admin">Admin</option>
          </select>
        </div>

        <div className="mb-4 flex items-center">
          <input
            type="checkbox"
            id="keepModalOpen"
            checked={keepModalOpen}
            onChange={(e) => setKeepModalOpen(e.target.checked)}
            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
          />
          <label htmlFor="keepModalOpen" className="ml-2 block text-sm text-gray-700">
            Keep modal open to invite multiple users
          </label>
        </div>

        {message && (
          <div className={`mb-4 p-2 rounded ${messageType === 'success' ? 'bg-green-50 text-green-700' : 'bg-blue-50 text-blue-700'}`}>
            {message}
          </div>
        )}

        {error && (
          <div className="mb-4 p-2 bg-red-50 text-red-700 rounded">
            {error}
          </div>
        )}

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => onUserInvited(undefined, true)}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-indigo-400"
          >
            {isLoading ? 'Sending...' : 'Send Invitation'}
          </button>
        </div>
      </form>
    </div>
  );
} 